import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'May 5, 2026';

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="font-display text-xl text-ink mb-3">{title}</h2>
      <div className="space-y-3 text-slate2 leading-relaxed">{children}</div>
    </div>
  );
}

export default function TermsOfService() {
  useEffect(() => {
    document.title = 'Terms of Service — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);

  return (
    <div className="w-full bg-bone">
      <div className="container-page pt-16 pb-12 max-w-3xl">
        <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine mb-4">
          Legal
        </div>
        <h1 className="font-display text-4xl text-ink leading-tight mb-2">
          Terms of Service
        </h1>
        <p className="text-slate2 text-sm mb-12">
          Effective date: {EFFECTIVE_DATE}
        </p>

        <Section title="Agreement">
          <p>
            By creating an account or purchasing a course on Learning Outcomes Academy
            ("we", "us", "the Academy"), you agree to these Terms of Service. Please read
            them before purchasing. If you do not agree, do not use the platform.
          </p>
          <p>
            Learning Outcomes Academy is operated from Oshawa, Ontario, Canada and is
            available to learners worldwide.
          </p>
        </Section>

        <Section title="Your account">
          <p>
            We use a magic-link sign-in system — no password required. When you request a
            sign-in link, it is sent to your email address and is valid for a single use.
            You are responsible for keeping access to your email account secure.
          </p>
          <p>
            Your account is personal and non-transferable. You may not share your account
            credentials or give others access to your purchased courses.
          </p>
        </Section>

        <Section title="Course access">
          <p>
            Upon successful payment, you receive lifetime access to the course content as
            it exists at the time of purchase, including any future updates we make to that
            course. "Lifetime" means for as long as the Academy operates and offers the
            course, subject to the termination provisions below.
          </p>
          <p>
            Course materials — including videos, lesson text, quizzes, and assessments —
            are licensed to you for personal, non-commercial use only. You may not:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Record, download, or redistribute any course content</li>
            <li>Share your account to allow others to access a course you purchased</li>
            <li>Use course content to create a competing product or service</li>
            <li>Reverse-engineer or scrape the platform</li>
          </ul>
        </Section>

        <Section title="Pricing and payment">
          <p>
            All prices are displayed in Canadian dollars (CAD) unless otherwise stated.
            Payment is processed securely by Stripe. We do not store your payment card
            information.
          </p>
          <p>
            Prices are subject to change. The price you pay at checkout is the price you
            pay — any future price changes do not affect existing purchases.
          </p>
          <p>
            Where applicable, taxes may be added at checkout based on your location and
            local tax law.
          </p>
        </Section>

        <Section title="Refund policy">
          <p>
            All course purchases are final. Due to the digital nature of our content,
            we do not offer refunds once access has been granted.
          </p>
          <p>
            If you experience a technical issue that prevents you from accessing your
            course, please reach out via our{' '}
            <Link to="/contact" className="text-ink underline underline-offset-2 hover:text-brand-deep">
              contact form
            </Link>{' '}
            and we will work to resolve it promptly.
          </p>
        </Section>

        <Section title="Certificate of completion">
          <p>
            A certificate of completion is issued upon passing the final assessment. The
            certificate reflects the name on your account at the time of completion. We
            do not backdate certificates or modify certificate names after issuance except
            to correct a verifiable error.
          </p>
          <p>
            Certificates are issued for educational purposes. They are not accredited
            professional credentials unless explicitly stated on the course page.
          </p>
        </Section>

        <Section title="Intellectual property">
          <p>
            All course content, including videos, text, graphics, quiz questions, and
            assessments, is owned by Learning Outcomes Academy or its licensors and is
            protected by Canadian and international copyright law. Your purchase grants
            you a limited, personal, non-exclusive, non-transferable licence to access
            and use the content for your own learning — nothing more.
          </p>
        </Section>

        <Section title="Platform availability">
          <p>
            We aim to keep the platform available at all times but cannot guarantee
            uninterrupted access. We may occasionally take the platform offline for
            maintenance. We are not liable for temporary outages.
          </p>
          <p>
            We reserve the right to discontinue a course with reasonable notice. If a
            course you have purchased is discontinued, we will offer a pro-rated refund
            or equivalent alternative access where feasible.
          </p>
        </Section>

        <Section title="Acceptable use">
          <p>
            You agree not to use the platform in any way that is unlawful, harmful, or
            disruptive to other users. We reserve the right to suspend or terminate
            accounts that violate these terms without refund.
          </p>
        </Section>

        <Section title="Limitation of liability">
          <p>
            To the maximum extent permitted by applicable law, Learning Outcomes Academy
            is not liable for any indirect, incidental, or consequential damages arising
            from your use of the platform. Our total liability for any claim related to a
            course purchase is limited to the amount you paid for that course.
          </p>
        </Section>

        <Section title="Governing law">
          <p>
            These Terms are governed by the laws of the Province of Ontario and the
            federal laws of Canada applicable therein, without regard to conflict of law
            principles. Any disputes shall be resolved in the courts of Ontario, Canada.
          </p>
          <p>
            If you are located in the European Union, you may also have rights under local
            consumer protection law that cannot be waived by contract.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            We may update these Terms from time to time. Material changes will be
            communicated by email and by updating the effective date above. Continued use
            of the platform after changes take effect constitutes acceptance of the
            revised Terms.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about these Terms? Please reach us via our{' '}
            <Link to="/contact" className="text-ink underline underline-offset-2 hover:text-brand-deep">
              contact form
            </Link>.
          </p>
        </Section>

        <div className="pt-6 border-t border-ink/10">
          <p className="text-sm text-slate2">
            By purchasing a course you confirm that you have read and agreed to these Terms of Service and our{' '}
            <Link to="/privacy" className="text-ink underline underline-offset-2 hover:text-brand-deep">
              Privacy Policy
            </Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
